// `/transactions/inbox` — two independent queues. Numeric badges come only
// from complete coverage. inbox-zero requires both complete zeroes. One
// failed queue stays degraded and does not erase the other.
import { useMemo, useState, type KeyboardEvent } from 'react';
import { RouteRecoveryScreen } from '@/app/states/RouteStates';
import { HonestInline } from '@/components/HonestInline';
import { showUndoToast } from '@/components/UndoToast';
import { useShellContext } from '@/app/shell/AppShell';
import {
  useInboxItems,
  type InboxCandidateReviewItem,
  type InboxUncategorizedItem,
  type InboxUncategorizedLineItem,
} from '@/api/hooks/useInboxItems';
import { useBatchUndo, type ApplyBatchInput } from '@/api/hooks/useBatchUndo';
import { useConfirmLink } from '@/api/hooks/useConfirmLink';
import { InboxUncategorizedRow } from './components/InboxUncategorizedRow';
import { TransferReviewCard } from './components/TransferReviewCard';
import { InboxBulkBar } from './components/InboxBulkBar';
import { InboxKeyboardLegend } from './components/InboxKeyboardLegend';
import { ViewSwitcher } from '@/features/transactions/ViewSwitcher';
import { SimilarList } from '@/features/transactions/SimilarList';
import { useCategoriesMap } from '@/api/hooks/useCategories';

function toUncategorizedRow(item: InboxUncategorizedLineItem): InboxUncategorizedItem {
  return {
    kind: 'uncategorized',
    transaction_id: item.transaction_id,
    merchant_display: item.merchant_display ?? 'Unknown merchant',
    merchant_raw: item.merchant_display ?? '',
    merchant_substituted: false,
    account_last4: null,
    posted_at: item.posted_at,
    pending: false,
    amount: item.amount,
    currency: item.currency,
    suggestion: null,
    business_ambiguous: false,
    business_hint: null,
    note: null,
  };
}

function countLabel(meta: { coverage: string; total_count: number | null } | undefined, failed: boolean): string {
  if (failed || !meta || meta.coverage !== 'complete' || meta.total_count === null) return 'unavailable';
  return String(meta.total_count);
}

function InboxPageContent() {
  const { band, readScope, scopeAvailability, retryScopeAvailability } = useShellContext();
  const canonicalReady = scopeAvailability === 'ready' && readScope === null;
  const inbox = useInboxItems(canonicalReady);
  const batchUndo = useBatchUndo();
  const confirmLink = useConfirmLink();
  const categoriesQuery = useCategoriesMap();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [writeError, setWriteError] = useState<string | null>(null);
  const [transferError, setTransferError] = useState<string | null>(null);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [lastBatchId, setLastBatchId] = useState<string | null>(null);
  const [hiddenCandidates, setHiddenCandidates] = useState<Set<string>>(new Set());

  const uncategorizedQuery = inbox.uncategorized;
  const candidatesQuery = inbox.candidates;
  const liveUncategorized = uncategorizedQuery?.data?.items ?? [];
  const uncategorized = useMemo(() => liveUncategorized.map(toUncategorizedRow), [liveUncategorized]);
  const transferReviews = useMemo(
    () => (candidatesQuery?.data?.items ?? []).filter((item) => !hiddenCandidates.has(item.candidate_id)),
    [candidatesQuery?.data?.items, hiddenCandidates],
  );

  const uncategorizedMeta = uncategorizedQuery?.data?.meta;
  const transferMeta = candidatesQuery?.data?.meta;
  const uncategorizedCount = uncategorizedMeta?.coverage === 'complete' ? uncategorizedMeta.total_count : null;
  const transferReviewCount = transferMeta?.coverage === 'complete' ? transferMeta.total_count : null;

  if (scopeAvailability !== 'ready') {
    return (
      <div>
        <ViewSwitcher />
        <p role="status" className="mt-4 rounded-md border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
          {scopeAvailability === 'pending' ? 'Checking transaction scope availability…' : <>Transaction scope availability could not be checked.{' '}<button type="button" className="min-h-11 rounded-md border border-input px-3 underline" onClick={retryScopeAvailability}>Retry</button></>}
        </p>
      </div>
    );
  }

  if (!canonicalReady) {
    return (
      <div>
        <ViewSwitcher />
        <p role="status" className="mt-4 rounded-md border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
          Inbox review is not available for this diagnostic scope.
        </p>
      </div>
    );
  }

  const bothLoading = Boolean(uncategorizedQuery?.isLoading && candidatesQuery?.isLoading);
  const bothFailed = Boolean(uncategorizedQuery?.isError && candidatesQuery?.isError);
  if (bothLoading && !uncategorizedQuery?.data && !candidatesQuery?.data) {
    return (
      <div>
        <ViewSwitcher />
        <div className="flex min-h-[30vh] items-center justify-center" role="status" aria-live="polite">
          <p className="text-sm text-muted-foreground">Loading inbox…</p>
        </div>
      </div>
    );
  }

  if (bothFailed) {
    return <div><ViewSwitcher /><RouteRecoveryScreen kind="api-unavailable" frame="inline" /></div>;
  }

  if (uncategorizedCount === 0 && transferReviewCount === 0) {
    return (
      <div className="mx-auto max-w-2xl py-6">
        <ViewSwitcher />
        <div className="mt-6">
          <HonestInline kind="inbox-zero" />
        </div>
      </div>
    );
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function applyBatch(input: ApplyBatchInput, messageOnSuccess: string) {
    setWriteError(null);
    batchUndo.apply.mutate(input, {
      onSuccess: (result) => {
        setLastBatchId(result.batch_id);
        showUndoToast({
          message: result.message || messageOnSuccess,
          batchId: result.batch_id,
          onUndo: (batchId) => {
            batchUndo.undo.mutate(batchId, {
              onError: () => setWriteError('Undo is not available yet in this build. Nothing was changed.'),
            });
          },
        });
      },
      onError: () => {
        setWriteError('Categorizing is not available yet in this build. Nothing was changed.');
      },
    });
  }

  function acceptSuggestion(item: InboxUncategorizedItem) {
    if (!item.suggestion) {
      setWriteError('This row has no suggested category. Use Apply to similar from the selected item.');
      return;
    }
    setWriteError(null);
  }

  function confirmTransfer(candidateId: string, kind: 'transfer' | 'cc_payment') {
    setTransferError(null);
    confirmLink.mutate(
      { candidateId, kind },
      { onError: () => setTransferError('The transfer could not be confirmed. Both rows remain visible.') },
    );
  }

  function chooseBusiness(item: InboxUncategorizedItem, choice: 'personal' | 'business') {
    applyBatch(
      {
        scope: 'single',
        business_use: choice === 'personal' ? 'none' : 'primary',
        transaction_ids: [item.transaction_id],
      },
      `Marked ${item.merchant_display} ${choice}`,
    );
  }

  const selectedItems = uncategorized.filter((item, index) => selectedIds.has(liveUncategorized[index]?.line_item_id ?? item.transaction_id));
  const sharedCategory =
    selectedItems.length > 1 && selectedItems.every((item) => item.suggestion)
      ? (() => {
          const first = selectedItems[0].suggestion!.path.join(' › ');
          return selectedItems.every((item) => item.suggestion!.path.join(' › ') === first) ? first : null;
        })()
      : null;

  function onKeyDown(event: KeyboardEvent) {
    if (band !== 'desktop' || uncategorized.length === 0) return;
    const key = event.key.toLowerCase();
    if (key === 'j') {
      setFocusedIndex((i) => Math.min(i + 1, uncategorized.length - 1));
    } else if (key === 'k') {
      setFocusedIndex((i) => Math.max(i - 1, 0));
    } else if (key === 'c') {
      const item = uncategorized[focusedIndex];
      if (item) acceptSuggestion(item);
    } else if (key === 'b') {
      const item = uncategorized[focusedIndex];
      if (item) chooseBusiness(item, 'business');
    } else if (key === 'u') {
      if (lastBatchId) {
        batchUndo.undo.mutate(lastBatchId, {
          onError: () => setWriteError('Undo is not available yet in this build. Nothing was changed.'),
        });
      }
    }
  }

  return (
    <div onKeyDown={onKeyDown} tabIndex={-1}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-normal text-muted-foreground" data-testid="inbox-count">
          · {countLabel(uncategorizedMeta, Boolean(uncategorizedQuery?.isError))} uncategorized · {countLabel(transferMeta, Boolean(candidatesQuery?.isError))} transfer review
        </span>
        <ViewSwitcher />
        {band === 'desktop' && <InboxKeyboardLegend />}
      </div>

      {writeError && (
        <div className="mt-3 rounded-md border border-degraded-border bg-degraded-surface px-3 py-2 text-sm text-degraded-foreground" role="alert">
          {writeError}
        </div>
      )}

      {uncategorizedQuery?.isError && (
        <p role="status" className="mt-3 rounded-md border border-degraded-border bg-degraded-surface px-3 py-2 text-sm text-degraded-foreground">
          Uncategorized count unavailable. Transfer review is still shown when it loaded.
        </p>
      )}
      {candidatesQuery?.isError && (
        <p role="status" className="mt-3 rounded-md border border-degraded-border bg-degraded-surface px-3 py-2 text-sm text-degraded-foreground">
          Transfer review unavailable. Uncategorized work is still shown when it loaded.
        </p>
      )}

      {!uncategorizedQuery?.isError && (uncategorizedCount === null || uncategorizedCount > 0) && uncategorized.length > 0 && (
        <section className="mt-4">
          <h2 className="text-xs uppercase tracking-wide text-muted-foreground">Uncategorized{uncategorizedCount === null ? '' : ` · ${uncategorizedCount}`}</h2>
          <div className={band === 'desktop' ? 'mt-2 rounded-lg border border-border' : ''}>
            {uncategorized.map((item, index) => {
              const live = liveUncategorized[index];
              const rowId = live?.line_item_id ?? item.transaction_id;
              return (
                <div key={rowId}>
                  <InboxUncategorizedRow
                    item={item}
                    band={band}
                    accepted={false}
                    selectable={band !== 'mobile'}
                    selected={selectedIds.has(rowId)}
                    onToggleSelect={() => toggleSelect(rowId)}
                    onAcceptSuggestion={() => acceptSuggestion(item)}
                    onOther={() => setFocusedIndex(index)}
                    onBusinessChoice={(choice) => chooseBusiness(item, choice)}
                  />
                  {band === 'desktop' && index === focusedIndex && <span className="sr-only">focused</span>}
                  {index === focusedIndex && live && live.category_id && (
                    <SimilarList
                      merchantName={item.merchant_display}
                      lineItemId={live.line_item_id}
                      lineItemVersion={live.version}
                      categoryId={live.category_id}
                      taxonomyVersion={categoriesQuery.data?.taxonomy_version}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {!candidatesQuery?.isError && transferReviews.length > 0 && (
        <section className="mt-6">
          <h2 className="text-xs uppercase tracking-wide text-muted-foreground">Transfer review</h2>
          {transferError && (
            <p role="alert" className="mt-2 text-sm text-destructive">
              {transferError}
            </p>
          )}
          <div className="mt-2 flex flex-col gap-2">
            {transferReviews.map((item: InboxCandidateReviewItem) => (
              <TransferReviewCard
                key={item.candidate_id}
                item={item}
                pending={confirmLink.isPending}
                onConfirm={(candidateId) => confirmTransfer(candidateId, item.link_kind)}
                onReject={(candidateId) => setHiddenCandidates((prev) => new Set(prev).add(candidateId))}
              />
            ))}
          </div>
        </section>
      )}

      {band !== 'mobile' && sharedCategory !== null && (
        <InboxBulkBar
          count={selectedItems.length}
          sharedCategoryLabel={sharedCategory}
          pending={batchUndo.apply.isPending}
          onApplyShared={() =>
            applyBatch(
              { scope: 'similar_this_month', category_path: sharedCategory.split(' › '), transaction_ids: Array.from(selectedIds) },
              `Applied ${sharedCategory} to ${selectedItems.length} items`,
            )
          }
          onOther={() => setWriteError('A category picker for bulk selections ships in a later cycle.')}
          onPersonal={() =>
            applyBatch(
              { scope: 'single', business_use: 'none', transaction_ids: Array.from(selectedIds) },
              `Marked ${selectedItems.length} items personal`,
            )
          }
        />
      )}
    </div>
  );
}

export function InboxPage() {
  return (
    <section aria-labelledby="inbox-heading" className="mx-auto max-w-5xl px-4 py-6">
      <h1 id="inbox-heading" tabIndex={-1} className="text-2xl font-bold">Inbox</h1>
      <InboxPageContent />
    </section>
  );
}

export default InboxPage;
